import React, { useState } from 'react';
import { ArrowLeft, Plus, RefreshCw, Coins, Users, Wallet } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { formatAmount } from '../utils/format';
import Modal from '../components/Modal';

const LoanDetail = () => {
  const {
    accounts,
    selectedLoan,
    loanTransactions,
    loading,
    error,
    setScreen,
    setError,
    setLoading,
    goToLoans,
    settleLoan,
    setInitialFormData
  } = useApp();

  const [showSettlementModal, setShowSettlementModal] = useState(false);
  const [settlementAmount, setSettlementAmount] = useState('');
  const [settlementAccountId, setSettlementAccountId] = useState('');

  const balance = parseFloat(selectedLoan?.balance) || 0;
  const isOwed = balance > 0;

  const handleAddLoanTransaction = () => {
    setInitialFormData({
      type: 'loan',
      loanContact: selectedLoan?.entity || '',
      loanDirection: isOwed ? 'lent' : 'borrowed'
    });
    setScreen('add');
  };

  const handlePayLoan = () => {
    setError('');
    setSettlementAmount(Math.abs(balance).toString());
    setSettlementAccountId('');
    setShowSettlementModal(true);
  };

  const handleSettleLoan = async () => {
    if (!settlementAmount) {
      setError('Please enter an amount');
      return;
    }

    if (!settlementAccountId) {
      setError('Please select an account');
      return;
    }

    try {
      await settleLoan(selectedLoan, settlementAmount, settlementAccountId);
      setShowSettlementModal(false);
      setSettlementAmount('');
      setSettlementAccountId('');
      setScreen('confirmation');
    } catch (err) {
      // Error handled in context
    }
  };

  if (!selectedLoan) {
    setScreen('loans');
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 pb-20">
      <div className="bg-white shadow-sm p-4 flex items-center">
        <button onClick={goToLoans} className="mr-4">
          <ArrowLeft className="w-6 h-6 text-gray-600" />
        </button>
        <h1 className="text-xl font-bold text-gray-800">Loan Details</h1>
      </div>

      <div className="p-6 space-y-4">
        {/* Balance Display */}
        <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
          <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${isOwed ? 'bg-green-100' : 'bg-red-100'}`}>
            <Users className={`w-8 h-8 ${isOwed ? 'text-green-600' : 'text-red-600'}`} />
          </div>
          <p className="text-lg font-medium text-gray-800 mb-2">{selectedLoan?.entity || 'Unknown'}</p>
          <p className={`text-4xl font-bold ${isOwed ? 'text-green-600' : 'text-red-600'}`}>
            ${formatAmount(balance)}
          </p>
          <p className={`text-sm mt-2 capitalize ${isOwed ? 'text-green-500' : 'text-red-500'}`}>
            {selectedLoan?.description || (isOwed ? 'owes you' : 'you owe')}
          </p>
        </div>

        {/* Loan Info */}
        <div className="bg-white rounded-2xl p-6 shadow-lg space-y-4">
          <div>
            <p className="text-xs text-gray-500 uppercase mb-1">Type</p>
            <p className="text-sm font-medium text-gray-800 capitalize">{selectedLoan?.type || 'Contact'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase mb-1">Entity</p>
            <p className="text-sm font-medium text-gray-800">{selectedLoan?.entity}</p>
          </div>
        </div>

        {/* Add Loan Transaction Button */}
        <button
          onClick={handleAddLoanTransaction}
          className="w-full bg-amber-600 text-white py-4 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-amber-700 transition"
        >
          <Plus className="w-5 h-5" />
          Add Loan Transaction
        </button>

        {/* Pay Loan / Settle Button */}
        <button
          onClick={handlePayLoan}
          className="w-full py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition bg-indigo-600 text-white hover:bg-indigo-700"
        >
          <Wallet className="w-5 h-5" />
          Pay Loan
        </button>

        {/* Loan Transactions */}
        <div className="bg-white rounded-2xl p-6 shadow-lg">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Transactions</h3>
          {loading ? (
            <div className="text-center py-4">
              <RefreshCw className="w-6 h-6 mx-auto text-indigo-600 animate-spin mb-2" />
              <p className="text-gray-500">Loading transactions...</p>
            </div>
          ) : loanTransactions.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-gray-500">No transactions found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {loanTransactions.map((tx) => {
                const amount = parseFloat(tx.amount) || 0;
                const isPositive = amount > 0;

                return (
                  <div
                    key={tx.id}
                    className="flex justify-between items-center p-3 bg-gray-50 rounded-xl"
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-full ${isPositive ? 'bg-green-100' : 'bg-red-100'}`}>
                        <Coins className={`w-4 h-4 ${isPositive ? 'text-green-600' : 'text-red-600'}`} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-800">
                          {tx.description || (tx.type === 'settlement' ? 'Settlement' : 'Loan')}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(tx.date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <p className={`font-bold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                      {isPositive ? '+' : '-'}${formatAmount(amount)}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Settlement Modal */}
      <Modal isOpen={showSettlementModal} onClose={() => setShowSettlementModal(false)}>
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center bg-indigo-100">
            <Wallet className="w-8 h-8 text-indigo-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">Pay Loan</h3>
          <p className="text-gray-600">
            {isOwed
              ? `${selectedLoan?.entity} owes you $${formatAmount(balance)}`
              : `You owe ${selectedLoan?.entity} $${formatAmount(balance)}`
            }
          </p>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Account</label>
          <select
            value={settlementAccountId}
            onChange={(e) => setSettlementAccountId(e.target.value)}
            className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="">Select account</option>
            {accounts.map((acc) => (
              <option key={acc.id} value={acc.id}>
                {acc.name} (${formatAmount(parseFloat(acc.balance))})
              </option>
            ))}
          </select>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
          <div className="flex items-center">
            <span className="text-2xl font-bold text-gray-400 mr-2">$</span>
            <input
              type="number"
              step="0.01"
              value={settlementAmount}
              onChange={(e) => setSettlementAmount(e.target.value)}
              className="flex-1 text-2xl font-bold text-gray-800 border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={() => {
              setShowSettlementModal(false);
              setSettlementAmount('');
              setSettlementAccountId('');
              setError('');
            }}
            className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-200 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSettleLoan}
            disabled={loading}
            className="flex-1 py-3 rounded-xl font-semibold transition disabled:bg-gray-400 bg-indigo-600 text-white hover:bg-indigo-700"
          >
            {loading ? 'Processing...' : 'Confirm'}
          </button>
        </div>
      </Modal>

      <button
        onClick={() => setScreen('add')}
        className="fixed bottom-6 right-6 bg-indigo-600 text-white p-4 rounded-full shadow-lg hover:bg-indigo-700 transition"
      >
        <Plus className="w-8 h-8" />
      </button>
    </div>
  );
};

export default LoanDetail;
